import { createContext, useContext } from 'react';

import type {
  Group,
  InstantSurvey,
  InstantSurveyType,
  Question,
  QuestionGroup,
} from '@api/instant_survey/types';
import type { QuestionnairePricingOption, Survey, SurveyDocumentType } from '@api/surveys/types';

export interface SurveyContextType {
  activeGroups: Group[];
  activeQuestions: Question[];
  activeStep: number;
  assignGroupToParent: (
    groupId: QuestionGroup['id'],
    parentGroupId: null | QuestionGroup['id']
  ) => Promise<void>;
  assignQuestionToGroup: (question: Question, groupId: null | QuestionGroup['id']) => Promise<void>;
  autoSaveError: null | unknown;
  copyGroup: (groupId: QuestionGroup['id']) => Promise<void>;
  createGroupForQuestion: (question: Question) => Promise<void>;
  currentQuestionCount: number;
  currentQuestionnairePrice?: QuestionnairePricingOption;
  deleteGroup: (groupId: QuestionGroup['id']) => Promise<void>;
  hasUnsavedChanges: boolean;
  insightsDocument?: Survey;
  insightsDocumentType: SurveyDocumentType;
  instantSurveyType: InstantSurveyType;
  isAutoSaving: boolean;
  isCopyingGroup: boolean;
  isLoadingSubscription: boolean;
  isReadOnly: boolean;
  isRealtimeSampleValidationEnabled: boolean;
  isResearchObjectivesTooLong: boolean;
  isSaveAsDraftOpen: boolean;
  isSavingAsDraft: boolean;
  isSubscribed: boolean;
  jobId: null | string;
  lastAutoSavedAt: Date | null;
  launchMethod: null | string;
  maxQuestions: number;
  persistSurveyDraft: (options?: {
    reason?: string;
    signal?: AbortSignal;
    targetVersion?: number;
  }) => Promise<null | {
    atlasSurveyResponse: Survey;
    instantSurveyResponse: InstantSurvey;
  }>;
  questionDisplayIndexById: Record<number, string>;
  renameGroup: (groupId: QuestionGroup['id'], name: string) => Promise<void>;
  saveInstantSurvey: (options?: { signal?: AbortSignal }) => Promise<InstantSurvey>;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setInsightsDocument: React.Dispatch<React.SetStateAction<Survey | undefined>>;
  setIsSaveAsDraftOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSavingAsDraft: React.Dispatch<React.SetStateAction<boolean>>;
  setJobId: React.Dispatch<React.SetStateAction<null | string>>;
  setLaunchMethod: (nextLaunchMethod: string, options?: { markDirty?: boolean }) => void;
  setShowAIGenerationModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowSurveyTemplateModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSurveyGoals: React.Dispatch<React.SetStateAction<string>>;
  showAIGenerationModal: boolean;
  showSurveyTemplateModal: boolean;
  subscription?: any | null; // TODO: replace with actual subscription type
  subscriptionError: null | unknown;
  survey: InstantSurvey;
  surveyGoals: string;
  toggleGroupRandomize: (groupId: QuestionGroup['id']) => Promise<void>;
  toggleQuestionAnchored: (question: Question) => void;
  updateSurvey: (fields: Partial<InstantSurvey>, options?: { markDirty: boolean }) => void;
  validatingGroupClientIds: string[];
}

export const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

// TODO: move SurveyContext.Provider here

export const useSurveyContext = (): SurveyContextType => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurveyContext must be used within a SurveyContext.Provider');
  }
  return context;
};
